import {
  CFormCheck,
  CModal,
  CModalBody,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import React, { useCallback, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import DeleteIcon from "../../components/icons/DeleteIcon";
import EditIcon from "../../components/icons/EditIcon";
import PromptModal from "../../components/modals/PromptModal";

import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import { useCookie } from "../../hooks/useCookie";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import { tdStyle, thStyle } from "../../helpers/constants";
import { Chip, Dash, IconAction } from "../../helpers/general";

/* ─────────────── Edit modal ─────────────── */

function EditCityModal({ city, onClose }: { city: City; onClose: () => void }) {
  const { token } = useCookie("vToken");
  const { toastError, toastSuccess } = useToast();
  const queryClient = useQueryClient();

  const [label, setLabel] = useState(city.label ?? "");
  const [latitude, setLatitude] = useState(String(city.latitude ?? ""));
  const [longitude, setLongitude] = useState(String(city.longitude ?? ""));
  const [isActive, setIsActive] = useState(Boolean(city.isActive));
  const [isEnabled, setIsEnabled] = useState(Boolean(city.isEnabled));

  const update = useMutation((data: any) =>
    apiService.MakePutRequest(`cities/${city.id}`, data, token),
  );

  const unchanged =
    label === (city.label ?? "") &&
    latitude === String(city.latitude ?? "") &&
    longitude === String(city.longitude ?? "") &&
    isActive === Boolean(city.isActive) &&
    isEnabled === Boolean(city.isEnabled);

  const invalid = !label.trim() || !latitude.trim() || !longitude.trim();

  const save = () =>
    update.mutate(
      { label: label.trim(), latitude, longitude, isActive, isEnabled },
      {
        onError: () => toastError(general.fr.message.operationFailed),
        onSuccess() {
          queryClient.invalidateQueries("getAllCities");
          toastSuccess(general.fr.message.cityUpdated);
          onClose();
        },
      },
    );

  return (
    <CModal visible onClose={onClose} alignment="center">
      <CModalBody>
        <h5 className="fs-5 mb-4 border-bottom pb-1">Mettre à jour la ville</h5>

        <div className="mb-3">
          <label htmlFor="city-label">Ville</label>
          <input
            id="city-label"
            type="text"
            className="custom-input w-100 mt-1"
            placeholder="Ville"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>

        <div className="row g-3 mb-3">
          <div className="col-6">
            <label htmlFor="city-long">Longitude</label>
            <input
              id="city-long"
              type="text"
              className="custom-input w-100 mt-1"
              placeholder="Longitude"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
            />
          </div>
          <div className="col-6">
            <label htmlFor="city-lat">Latitude</label>
            <input
              id="city-lat"
              type="text"
              className="custom-input w-100 mt-1"
              placeholder="Latitude"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
            />
          </div>
        </div>

        <div className="d-flex gap-4 mb-3">
          <CFormCheck
            id="city-active"
            label="Active"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          <CFormCheck
            id="city-enabled"
            label="Activée (enabled)"
            checked={isEnabled}
            onChange={(e) => setIsEnabled(e.target.checked)}
          />
        </div>

        <button
          type="button"
          onClick={save}
          disabled={update.isLoading || unchanged || invalid}
          className="d-flex align-items-center justify-content-center btn btn-danger shadow-primary w-100 mt-2 p-2 fw-bold text-white"
        >
          {update.isLoading ? (
            <>
              <CSpinner size="sm" /> {general.fr.wait}
            </>
          ) : (
            "Enregistrer"
          )}
        </button>
      </CModalBody>
    </CModal>
  );
}

/* ─────────────── Row ─────────────── */

interface RowProps {
  city: City;
  deletedList: React.Dispatch<BulkDeleteActions>;
  isDeleting: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function CityRow({
  city,
  deletedList,
  isDeleting,
  onEdit,
  onDelete,
}: RowProps) {
  const [isChecked, setIsChecked] = useState(false);

  const toggle = useCallback(
    (checked: boolean) => {
      setIsChecked(checked);
      deletedList({
        type: checked ? DELETE_ACTIONS.ADD : DELETE_ACTIONS.REMOVE,
        id: city.id,
      });
    },
    [deletedList, city.id],
  );

  // A row that unmounts (page change, search) must not stay selected
  useEffect(
    () => () => deletedList({ type: DELETE_ACTIONS.REMOVE, id: city.id }),
    [deletedList, city.id],
  );

  const hasCoords = city.latitude != null && city.longitude != null;

  return (
    <CTableRow
      style={{
        backgroundColor: isChecked ? "rgba(240,129,63,0.06)" : undefined,
        boxShadow: isChecked ? `inset 3px 0 0 ${colors.primary}` : undefined,
      }}
    >
      <CTableDataCell style={tdStyle}>
        <div
          className="d-flex align-items-center gap-3"
          style={{ minWidth: 200 }}
        >
          <CFormCheck
            checked={isChecked}
            onChange={(e) => toggle(e.target.checked)}
          />
          <span className="fw-semibold text-capitalize">{city.label}</span>
        </div>
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        <div className="d-flex flex-wrap gap-1">
          <Chip tone={city.isActive ? "success" : "danger"}>
            {city.isActive ? "Active" : "Inactive"}
          </Chip>
          <Chip tone={city.isEnabled ? "success" : "neutral"}>
            {city.isEnabled ? "Activée" : "Désactivée"}
          </Chip>
        </div>
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        {hasCoords ? (
          <a
            href={`https://www.google.com/maps?q=${city.latitude},${city.longitude}`}
            target="_blank"
            rel="noreferrer noopener"
            className="font-monospace"
            style={{ fontSize: 12, whiteSpace: "nowrap" }}
          >
            {city.latitude}, {city.longitude}
          </a>
        ) : (
          <Dash />
        )}
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        <div className="d-flex align-items-center justify-content-end gap-2">
          <IconAction title="Modifier" onClick={onEdit}>
            <EditIcon width="16" height="16" fill={colors.secondary} />
          </IconAction>
          <IconAction
            title="Supprimer"
            danger
            onClick={onDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <CSpinner size="sm" />
            ) : (
              <DeleteIcon width="18" height="18" fill={colors.primary} />
            )}
          </IconAction>
        </div>
      </CTableDataCell>
    </CTableRow>
  );
}

/* ─────────────── Table ─────────────── */

interface TableProps {
  Cities: City[];
  CitiesToBeDeletedList: React.Dispatch<BulkDeleteActions>;
}

export default function CitiesTable({
  Cities,
  CitiesToBeDeletedList,
}: TableProps) {
  const { token } = useCookie("vToken");
  const { toastSuccess, toastError } = useToast();
  const queryClient = useQueryClient();
  const [toEdit, setToEdit] = useState<City | null>(null);
  const [toDelete, setToDelete] = useState<City | null>(null);

  const deleteMutation = useMutation((id: string) =>
    apiService.MakeDeleteRequest(`cities/${id}`, token),
  );

  const confirmDelete = () => {
    if (!toDelete) return;
    deleteMutation.mutate(toDelete.id, {
      onError: () => toastError(general.fr.message.operationFailed),
      onSuccess() {
        toastSuccess(general.fr.message.placeDeleted);
        queryClient.invalidateQueries("getAllCities");
      },
    });
    setToDelete(null);
  };

  const deletingId = deleteMutation.isLoading
    ? deleteMutation.variables
    : undefined;

  return (
    <div>
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell style={thStyle}>Ville</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Statut</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Coordonnées</CTableHeaderCell>
            <CTableHeaderCell style={{ ...thStyle, textAlign: "right" }}>
              Actions
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {Cities?.map((city) => (
            <CityRow
              key={city.id}
              city={city}
              deletedList={CitiesToBeDeletedList}
              isDeleting={deletingId === city.id}
              onEdit={() => setToEdit(city)}
              onDelete={() => setToDelete(city)}
            />
          ))}
        </CTableBody>
      </CTable>

      {toEdit && (
        <EditCityModal
          key={toEdit.id}
          city={toEdit}
          onClose={() => setToEdit(null)}
        />
      )}

      <PromptModal
        open={toDelete !== null}
        onAccept={confirmDelete}
        onClose={() => setToDelete(null)}
      />
    </div>
  );
}
